// ** React Imports

import { useCallback, useState, useEffect, Fragment, useRef } from 'react'



import { useTranslation } from 'src/utils/i18n'

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'

// ** MUI Imports
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

// ** Email App Component Imports
import MailLog from 'src/views/android/email/MailLog'

// ** Actions
import { fetchMails, fetchSelectedMails, setMailDateRange } from 'src/store/apps/android-email'
import { selectDeviceRequestKey } from 'src/store/apps/Common'

import { Pagination } from 'antd'

import styles from './index.module.scss'
import { setCurrentTab } from 'src/store/apps/Common'

import Image from 'next/image'
import Head from 'next/head'
import Icon from 'src/@core/components/icon'
import { DatePicker } from 'antd'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'
import ContentLoading from 'src/components/ContentLoading'
import { buildTimeParams } from 'src/utils/dateRangeParams'

const { RangePicker } = DatePicker

const CustomRangePicker = styled(RangePicker)`
  .ant-picker-input {// 包裹显示两个时间input的div样式
    width:auto;
  }
  .ant-picker-input > input {// 显示两个时间的input样式
    font-family: Poppins !important;
    font-weight: 400 !important;
    line-height: 24px !important;
    font-size: 16px !important;
    text-align: center;
    text-overflow: clip !important;  /* 禁止使用省略号 */
    overflow: visible !important;    /* 允许文本溢出 */
  }
  .ant-picker-input input[date-range="start"] {
  width: var(--firstWidth) !important;
  }
  .ant-picker-input input[date-range="end"] {
    width: var(--lastWidth) !important;
  }
  .ant-picker-input > input::placeholder {// 显示两个时间的placeholder样式（占位符
    color:#ADADAD;
  }
  .ant-picker-suffix {// 框内icon样式
    order:-1;
    opacity:1 !important;
    margin-right:11px;
  }
  `

const ScrollDiv = styled('ul')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const EmailAppLayout = () => {
  let Basic = window.localStorage.getItem('Basic')
  Basic = Basic == null || Basic == 0 ? true : false

  // ** States
  const [composeOpen, setComposeOpen] = useState(false)
  const [mailDetailsOpen, setMailDetailsOpen] = useState(false)

  // ** Hooks
  const theme = useTheme()

  const dispatch = useDispatch()

  const [mailBoxes, setMailBoxes] = useState(0) // 移动端显示层级
  const allEmails = useSelector(state => state.androidEmail.allEmails) // 全部邮件列表
  const [emailList, setEmailList] = useState([]) // 当前显示的邮件列表
  const selectedMails = useSelector(state => state.androidEmail.selectedMails) // 全部邮件列表
  const [antdSelectedDates, setAntdSelectedDates] = useState([])

  const hidden = useMediaQuery(theme.breakpoints.down('lg'))
  const store = useSelector(state => state.androidEmail)
  const deviceRequestKey = useSelector(selectDeviceRequestKey)

  const toggleComposeOpen = () => setComposeOpen(!composeOpen)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  useEffect(() => {
    setCurrentPage(1)
    setPageSize(50)
    setMailBoxes(0)
    setMailDetailsOpen(false)
    setAntdSelectedDates([])
  }, [deviceRequestKey])

  useEffect(() => {
    dispatch(fetchMails(currentPage, pageSize))
  }, [currentPage, pageSize, deviceRequestKey, dispatch])

  // 改每页条数：页码重置为 1，Pagination 会按 total/pageSize 重算总页数
  const handlePaginationChange = (page, size) => {
    const nextSize = Number(size) || pageSize
    if (nextSize !== pageSize) {
      setPageSize(nextSize)
      setCurrentPage(1)

      return
    }
    setCurrentPage(page)
  }

  const { t } = useTranslation('common')

  useEffect(() => {
    dispatch(setCurrentTab(t('Email')))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  const dom = useRef(null)
  const [scroll, setScroll] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = dom.current
      const progress = scrollTop / (scrollHeight - clientHeight) * 100
      setScroll(progress.toFixed(0))
    }

    const currentRef = dom.current
    if (currentRef) currentRef.addEventListener('scroll', handleScroll)

    return () => {
      if (currentRef) currentRef.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (scroll >= 100 && store.total !== allEmails.length) setCurrentPage(page => page + 1)
  }, [allEmails.length, scroll, store.total])

  // 获取今天的日期
  const today = dayjs().format('YYYY-MM-DD')

  // 获取七天前的日期
  const sevenDaysAgo = dayjs().subtract(7, 'day').format('YYYY-MM-DD')

  // 列表跟随 store（日期筛选由接口完成）
  useEffect(() => {
    setEmailList(allEmails)
  }, [allEmails])

  //选择日期范围
  const handleDateChange = useCallback(
    dates => {
      const picked = dates && dates.length === 2 ? dates : []

      setAntdSelectedDates(picked)

      // 把区间交给 store，由接口按日期重新取数
      const { startTime, endTime } = buildTimeParams(picked)
      dispatch(setMailDateRange(picked.length === 2 ? { startTime, endTime } : null))

      // 回到第一页重新请求；页码本来就等于 1 时 effect 不会触发，这里手动补一次
      if (currentPage !== 1) setCurrentPage(1)
      else dispatch(fetchMails(1, pageSize))
    },
    [currentPage, dispatch, pageSize]
  )

  // 获取最宽日期选择器占位符宽度
  useEffect(() => {

    // 开始时间&结束时间
    let firstDate = '0000-00-00'
    let lastDate = '0000-00-00'

    // 开始时间宽度&结束时间宽度
    let firstWidth = 0
    let lastWidth = 0

    const span = document.createElement('span')
    span.style.setProperty('font-family', 'Poppins', 'important')
    span.style.setProperty('font-size', '16px', 'important')
    span.style.setProperty('font-weight', '400', 'important')
    span.style.setProperty('line-height', '24px', 'important')
    span.style.visibility = 'hidden'
    span.innerText = firstDate

    // 获取第一个占位符宽度
    document.body.appendChild(span)
    firstWidth = span.offsetWidth
    document.body.removeChild(span)

    // 获取第二个占位符宽度
    span.innerText = lastDate
    document.body.appendChild(span)
    lastWidth = span.offsetWidth
    document.body.removeChild(span)

    // 插入至html标签样式中用于调用
    document.documentElement.style.setProperty('--firstWidth', `${firstWidth}px`)
    document.documentElement.style.setProperty('--lastWidth', `${lastWidth}px`)
  }, [])

  return (
    <Fragment>
      <Head>
        <meta name="format-detection" content="telephone=no" />
      </Head>
      {store.loading && allEmails.length === 0 ? (
        <div className='relative bg-[#FFF] drop-shadow-[0_0_12px_rgba(0,0,0,0.13)] rounded-[10px] Bxl:h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] flex'>
          <ContentLoading />
        </div>
      ) : (
      <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          '@media (max-width: 990px)': {
            display: 'none'
          }
        }}
      >
        <div
          className={styles.box}
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            marginBottom: '10px',
            alignItems: 'center'
          }}
        >
          <Pagination
            className='ml-[10px]'
            current={currentPage}
            pageSize={pageSize}
            total={store.total}
            showSizeChanger
            pageSizeOptions={['20', '50', '100']}
            style={{ display: 'flex', alignItems: 'center' }}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
          />
          <span style={{ color: '#5e5e5e', fontSize: '14px' }}>
            {t('Total')}: {store.total}
          </span>
        </div>
        <MailLog
          store={store}
          hidden={hidden}
          dispatch={dispatch}
          mailDetailsOpen={mailDetailsOpen}
          setMailDetailsOpen={setMailDetailsOpen}
        />
      </Box>

      {/* 移动端新ui */}
      <div
        className={`relative bg-[#FFF] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] Bxl:h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] m-auto hidden smj:flex flex-col`}
      >
        {mailBoxes === 0 && (
          <>
            <div>
              <div style={{ display: 'flex', color: '#000000', margin: 'auto' }} className='py-[21px] w-[90%]'>
                <span
                  style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    lineHeight: '27px',
                    fontWeight: '500',
                    margin: 'auto'
                  }}
                >
                  {t('Mail')}
                </span>
              </div>
              {/*时间选择器*/}
              {emailList && (
                <Fragment>
                  {/* 新ui */}
                  <div className='w-full px-[15px] mb-2'>
                    <CustomRangePicker
                      format="YYYY-MM-DD" // 左右两边显示的日期格式
                      placeholder={[sevenDaysAgo, today]} // 默认显示占位符
                      separator={<span className={`text-[#ADADAD]`}>~</span>} // 两个日期的间隔符
                      suffixIcon={<Image src="/images/calls/calendar.svg" alt='calendar' width={18} height={18} />} // 替换默认icon
                      className='w-full h-[40px] bg-[#F8F8F8]'
                      inputReadOnly={true} // 禁用手机弹出输入法 true为不允许弹出
                      // allowClear={false} // 清除按钮是否显示
                      onChange={handleDateChange}
                    />
                  </div>
                </Fragment>
              )}
            </div>

            {/* 新ui */}
            <ScrollDiv ref={dom} className='w-full px-[15px] overflow-auto'>
              {emailList.map((row, i) => (
                <li
                  key={i}
                  className='w-full py-[8px] [border-bottom:1px_solid_#E5E7EB] cursor-pointer'
                  onClick={() => {
                    dispatch(fetchSelectedMails(row))
                    setMailBoxes(1)
                    setTimeout(() => { }, 600)
                  }}
                >
                  <div style={{ paddingLeft: '3px', display: 'flex', justifyContent: 'space-between' }}>
                    <p style={{ fontWeight: '600', fontSize: '16px', lineHeight: '24px', color: '#000000', maxWidth: '65%', wordBreak: 'break-all' }}>{row.source}</p>
                    <p style={{ fontWeight: '400', color: '#808080', fontSize: '12px', lineHeight: '18px', paddingRight: '3px' }}>{dayjs(row.time).format('YYYY/MM/DD')}</p>
                  </div>
                  <div style={{ fontWeight: '400', paddingLeft: '3px', fontSize: '13px', lineHeight: '19.5px', color: '#222222' }}>
                    <p>{row.title}</p>
                  </div>
                  {/* <div
                    style={{
                      paddingLeft: '3px',
                      color: '#808080',
                      fontSize: '13px',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                      lineHeight: '19px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: '2'
                    }}
                  >
                    <p>{row.subject}</p>
                  </div> */}
                </li>
              ))}
            </ScrollDiv>
          </>
        )}
        {mailBoxes === 1 && (
          <>
            <div className=' Bxl:h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] flex flex-col'>
              <div
                style={{ display: 'flex', color: '#000000', margin: 'auto', paddingLeft: '16px' }}
                className='py-[21px] w-[100%]'
              >
                <Icon icon='ion:chevron-back' onClick={() => setMailBoxes(0)} />{' '}
                <span
                  style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    lineHeight: '27px',
                    fontWeight: '600',
                    margin: 'auto'
                  }}
                ></span>
                <div className='w-[24px] h-[24px]'></div>
              </div>

              {/* 新ui */}
              <div
                style={{
                  width: '100%',
                  margin: 'auto',
                  height: 'auto',
                  borderBottom: '1px solid #E4E4E6',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '16px',
                  paddingBottom: '12px'
                }}
              >
                <div
                  style={{
                    width: '35px',
                    height: '35px',
                    background: 'linear-gradient(180deg, #A5AAB6 0%, #868A93 100%)',
                    borderRadius: '50%',
                    textAlign: 'center',
                    lineHeight: '24px',
                    fontWeight: '500',
                    fontSize: '16px',
                    color: '#fff',
                    marginRight: '14px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {selectedMails.fromName.split(' ').map(word => word[0]).join('')}
                </div>
                <div className='flex-1'>
                  <div style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'start' }}>
                    <p style={{ fontWeight: '600', fontSize: '16px', lineHeight: '24px', maxWidth: '65%', wordBreak: 'break-all' }}>{selectedMails.fromName}</p>
                    <p style={{ color: 'rgba(0, 0, 0, 0.56)', fontSize: '14PX', lineHeight: '21px', paddingRight: '12px' }}>{dayjs(selectedMails.time).format('YYYY/MM/DD')}</p>
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '21px', fontWeight: '400', display: 'flex', alignItems: 'start' }}>
                    <p>{t('Receipt')}:</p>&nbsp;
                    <p className='text-[#959595] max-w-[55%] break-all'>
                      {selectedMails.toName}
                      <Image src='/images/mail/mailunion.svg' width={7} height={7} className='inline-block ml-2' alt='' />
                    </p>
                  </div>
                </div>
              </div>
              <div className='flex-1 overflow-auto rounded-[10px]' dangerouslySetInnerHTML={{ __html: selectedMails.content }} />
            </div>
          </>
        )}
      </div>
      </>
      )}
    </Fragment>
  )
}

export default EmailAppLayout


